import { CollectionNameEnum, DocumentTypeEnum, Employee, Expense, ExpenseKindEnum, ExpenseStatusEnum, OperationTypeEnum, UploadKindEnum } from "@finway-group/shared/lib/models"
import { GetEmployeeById } from "@finway-group/shared/lib/models/user/employee.model"
import { Alert, Button, Card, Col, Divider, Dropdown, Menu, Row, Tag, Tooltip } from "antd"
import moment from "moment"
import React, { Suspense, useEffect, useMemo, useState } from "react"
import { AlertTriangle, Archive as ArchiveIcon, Download as DownloadIcon, Edit2 as EditIcon } from "react-feather"
import { Helmet } from "react-helmet"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useHistory, useParams } from "react-router-dom"

import AdditionalExpenseOptionButton from "Components/additionalExpenseOptionsButton"
import ExpenseSwitching from "Components/expenseSwitching"
import ExpenseTagSection from "Components/expenseTagSection"
import ExportDropDownMenu from "Components/exportDropDownMenu"
import ExportTag from "Components/exportTag"
import MileageRequestMap from "Components/forms/expenseForm/reimbursementForms/mileage/mileageRequestMap"
import { PerDiemReimbursementsPerDayTable } from "Components/forms/expenseForm/reimbursementForms/perDiem/perDiemReimbursementsPerDayTable"
import Loading from "Components/loading"

import ExpenseCreateFormModal from "Components/modals/expenseCreateForm.modal"
import InboxInvoiceModal from "Components/modals/inboxInvoice/inboxInvoice.modal"
import PaymentConfirmationModal from "Components/modals/paymentConfirmation.modal"
import PageCustomHeader from "Components/PageCustomHeader"
import WorkflowIndicator from "Components/workflowIndicator"
import { ExpenseExport } from "Features/export/expenseExport"
import { shouldAdjustLimit } from "Features/pages/creditCards/creditCard.utils"
import { isUserPartOfExpenseWorkflow, shouldShowWorkflowIndicator } from "Features/pages/workflows/workflow.utils"
import { useArchiveInterval, useCompany } from "Shared/hooks/company.hooks"
import { useCostCenterById } from "Shared/hooks/costCenter.hooks"
import { useCostCenter2ById } from "Shared/hooks/costCenter2.hooks"
import { useCardById } from "Shared/hooks/creditCard.hooks"
import { useEmployees } from "Shared/hooks/employee.hooks"
import { useExpense, useExpenseSwitching } from "Shared/hooks/expense.hooks"
import { useTaxes } from "Shared/hooks/tax.hooks"
import { useCreditorById, useVendors } from "Shared/hooks/vendor.hooks"
import { useWorkflows } from "Shared/hooks/workflow.hooks"
import { BankService, FileService, NotificationService, OcrService, UserService, VendorService } from "Shared/services"
import DialogService from "Shared/services/dialog.service"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { ThunkDispatchResult } from "Shared/store"
import { fetchOneExpense, setExpense, updateDocuments, updateExpense } from "Shared/store/actions/expense/expenseActions"
import { getPopupAnchor } from "Shared/utils"
import { checkFileSize, isExtendedAlphanumeric } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { buildOcrUpdateData } from "Shared/utils/ocr.utils"

import { UserLanguageEnum } from "@finway-group/shared/lib/models/user/userSettings.interface"
import { FINWAY_ADMIN_EMAIL } from "Shared/config/consts"
import {
    approveExpenseRequest,
    cancelExpenseRequest,
    checkForDuplicate,
    checkForDuplicateInvoiceFile,
    confirmDatevWarning,
    deleteUnnecessaryExpenseFormData,
    isExpenseArchived,
    isExpenseManuallyUnarchived,
    isExpenseSwitchingShown,
    isMatched,
    isMileageExpense,
    isPerDiemExpense,
    markExpenseAsPaid,
    markExpenseAsReviewed,
    sendEmailReminder,
    showPaymentFailedAlert,
} from "../expenseUtils"
import ExpenseCreditorAlert from "./expenseCreditorAlert"
import ExpenseDocumentSection from "./expenseDocumentSection"
import ExpenseRightSection from "./expenseRightSection"
import { getTooltipPopupContainer } from "Shared/utils/helpers"
import { TablesEnum } from "Shared/store/reducers/tableConfigReducer"
import { refetchTable } from "Shared/store/actions/tables/tableActions"
import MainDetailsTabs from "./mainDetailsTabs"
import { ReadOnlyContext } from "Shared/context/readOnly.context"
import SharedExpenseTag from "Components/sharedExpenseTag"

const CommentContainer = React.lazy(() => import("Components/comment/comment.container"))

const ExpenseDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const { t } = useTranslation()
    const dispatch: ThunkDispatchResult = useDispatch()
    const history = useHistory()

    const [isInvoiceUploading, setIsInvoiceUploading] = useStateIfMounted<boolean>(false)
    const [isDeliveryNoteUploading, setIsDeliveryNoteUploading] = useStateIfMounted<boolean>(false)
    const [isLoading, setIsLoading] = useStateIfMounted<boolean>(false)
    const [isEditModalShowing, setIsEditModalShowing] = useState<boolean>(false)
    const [showInboxModal, setShowInboxModal] = useState(false)
    const [isShowingPaymentConfirmationModal, setIsShowingPaymentConfirmationModal] = useState<boolean>(false)
    const [isScheduledPayment, setIsScheduledPayment] = useStateIfMounted<boolean>(false)
    const [isSummaryDownloadLoading, setSummaryDownloadLoading] = useStateIfMounted<boolean>(false)

    let expense = useExpense()
    const { card } = useCardById(expense.card)

    const employees = useEmployees(false, false, true)
    const vendor = useCreditorById(expense.vendor)
    const loggedInUser = UserService.getLoggedInEmployeeProfile()
    const [isOcrEnabled, setIsOcrEnabled] = useStateIfMounted(loggedInUser.settings.enableOcr)
    const [isOcrItemizationEnabled, setIsOcrItemizationEnabled] = useStateIfMounted(loggedInUser.settings.enableOcrItemization)
    const [isOcrUploading, setIsOcrUploading] = useStateIfMounted(false)
    const [isLoadingExpense, setIsLoadingExpense] = useStateIfMounted(false)
    const [isExpenseUpdating, setIsExpenseUpdating] = useStateIfMounted(false)

    const vendors = useVendors(true)
    const taxes = useTaxes()
    const isEditableExpense =
        expense.status === ExpenseStatusEnum.PURCHASE_PENDING || expense.status === ExpenseStatusEnum.INVOICE_PENDING || expense.status === ExpenseStatusEnum.DOCS_NEEDED
    const costCenter = useCostCenterById(expense.costCenter)!
    const costCenter2 = useCostCenter2ById(expense.costCenter2)
    const approver = GetEmployeeById(employees, expense.approvalNeededBy) || new Employee()
    const requestedBy = GetEmployeeById(employees, expense.requestedBy) || new Employee()
    const isMyExpense = expense.requestedBy === loggedInUser.id
    const isApprover = expense.approvalNeededBy === loggedInUser.id || expense.secondApprovalNeededBy === loggedInUser.id
    const isAdmin = loggedInUser.activeCompanyProfile.isAdmin
    const isReadOnly = !isAdmin && !isApprover && !isMyExpense
    const taggedUsers = expense.userTagging?.map((tagging) => tagging.tagged)
    const displayUserAsTagged = isReadOnly && taggedUsers?.includes(loggedInUser.id)

    const requestType = expense.getRequestType()
    const isEditButtonDisabled =
        (expense.status === ExpenseStatusEnum.PAID && !loggedInUser.activeCompanyProfile.isAdmin) || expense.status === ExpenseStatusEnum.DONE || expense.deleted
    const splitsHasCompleteCostCenter = expense.splits?.length === 0 || expense.splits?.every((s) => s.costCenter)
    const splitsHasAnyCostCenter = expense.splits?.some((s: any) => s.costCenter)
    const workflows = useWorkflows()
    const isPartOfAttachedWorkflow = isUserPartOfExpenseWorkflow(expense, workflows, loggedInUser.id)
    const archiveAfterXDays = useArchiveInterval()
    const { taggingEnabled } = useCompany()

    /*useMemo is not used for performance reasons but to meet the desired functionality for expense switching */
    const expenseStatusMemoized = useMemo(() => expense.status, [expense.id])
    const showExpenseSwitching = useMemo(() => isExpenseSwitchingShown(expense, archiveAfterXDays), [expense.id])

    const { prevExpense, nextExpense, isExpenseSwitchingLoading, hasError, adjacentExpenseLabel } = useExpenseSwitching(expense.id, expenseStatusMemoized, expense.requestedBy)
    const editTooltipProp = !isEditButtonDisabled && {
        visible: isEditButtonDisabled,
        title: expense.status === ExpenseStatusEnum.DONE,
    }

    // TODO_TRM : Remove this once we can attach invoices automatically to the following kinds
    const forceShowExportDropdownMenu = [ExpenseKindEnum.PER_DIEM, ExpenseKindEnum.MILEAGE].includes(expense.kind)

    useEffect(() => {
        setIsLoadingExpense(true)
        dispatch(fetchOneExpense(id))
            .then(() => setIsLoadingExpense(false)) // it is not necessary to setLoading to false in a .finally() since if there is an error we unmount the component
            .catch((_error) => {
                history.push("/expenses")
            })
        dispatch(refetchTable(TablesEnum.EXPENSE_TRANSACTIONS))
    }, [id])

    const onCancelRequest = () => {
        cancelExpenseRequest(expense, true, () => {
            history.goBack()
        })
    }

    const onSendEmailReminder = () => sendEmailReminder(loggedInUser.id, employees, expense)

    const markRequestAsApproved = (expense: Expense, userId: string) => {
        setIsLoading(true)
        approveExpenseRequest(expense)
            .then(() => {
                loggedInUser.settings.enableGoToNextExpense && nextExpense && history.push({ pathname: nextExpense, state: { fromExpenseSwitching: true } })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const markRequestAsReviewed = () => {
        if (expense.invoiceNumber && !isExtendedAlphanumeric(expense.invoiceNumber)) confirmDatevWarning(onMarkAsReviewed)
        else onMarkAsReviewed()
    }

    const onMarkAsReviewed = () => {
        setIsLoading(true)
        markExpenseAsReviewed(expense)
            .then(() => {
                loggedInUser.settings.enableGoToNextExpense && nextExpense && history.push({ pathname: nextExpense, state: { fromExpenseSwitching: true } })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const markRequestAsPaid = () => {
        setIsLoading(true)
        markExpenseAsPaid(expense).finally(() => setIsLoading(false))
    }

    const payExpense = (isScheduledPayment: boolean) => {
        setIsShowingPaymentConfirmationModal(true)
        setIsScheduledPayment(isScheduledPayment)
    }

    const onInboxSelected = (e: any) => {
        e.stopPropagation()
        setShowInboxModal(true)
    }

    const onlyFileExport =
        ![ExpenseStatusEnum.REVIEWED, ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE].includes(expense.status) || !loggedInUser.activeCompanyProfile.isAdmin || expense.deleted

    const renderActionButtons = (
        <div className="flex-wrap flex">
            {taggingEnabled && <ExpenseTagSection expenseId={expense.id} expenseTags={expense.expenseTags} />}
            {(isEditableExpense && (isMyExpense || isApprover || isPartOfAttachedWorkflow)) || loggedInUser.activeCompanyProfile.isAdmin ? (
                <>
                    {shouldShowWorkflowIndicator(expense, workflows) && <WorkflowIndicator expense={expense} />}
                    <Tooltip
                        getPopupContainer={getTooltipPopupContainer}
                        {...editTooltipProp}
                        title={<div>{t("tooltips:expenses.not_editable_exported")}</div>}
                        placement="bottomLeft"
                    >
                        <Button className="m-5 btn-default" disabled={isEditButtonDisabled} onClick={() => setIsEditModalShowing(true)}>
                            <EditIcon />
                            <span>{t("action:edit")}</span>
                        </Button>
                    </Tooltip>
                    {(expense.invoices?.length > 0 || forceShowExportDropdownMenu) && (
                        <ExpenseExport
                            render={({ onExport, isExporting }) => (
                                <ExportDropDownMenu
                                    onExport={onExport}
                                    expenses={[expense]}
                                    isLoading={isExporting}
                                    buttonClassName={"m-5"}
                                    includeSepa
                                    onlyFileExport={onlyFileExport}
                                />
                            )}
                        />
                    )}
                    {loggedInUser.activeCompanyProfile.isAdmin && <AdditionalExpenseOptionButton expense={expense} isEditDisabled={!!isEditButtonDisabled} />}
                </>
            ) : null}
            {showExpenseSwitching && (
                <ExpenseSwitching
                    hasError={hasError}
                    isLoading={isExpenseSwitchingLoading}
                    prevExpense={prevExpense}
                    nextExpense={nextExpense}
                    adjacentExpenseLabel={adjacentExpenseLabel}
                />
            )}
        </div>
    )

    const runOcr = async (invoiceUrl: string) => {
        try {
            setIsOcrUploading(true)
            const { ocrObject } = await OcrService.uploadAndRetrieve(invoiceUrl)

            // see if we have a duplicate and should remove the invoice number from the update
            if (ocrObject?.invoiceData?.number) {
                if (!(await checkForDuplicate(expense, ocrObject?.invoiceData?.number))) delete ocrObject?.invoiceData?.number
            }

            onUpdateExpenseFromOcr(ocrObject)
        } catch (err) {
            NotificationService.send(NotificationTypeEnum.WARNING, t("error:ocr.title"), t("error:ocr.message"))
        } finally {
            setIsOcrUploading(false)
        }
    }

    const onUpdateExpenseFromOcr = async (ocrObject: any) => {
        try {
            const updateData = buildOcrUpdateData(ocrObject, expense, vendors, taxes, loggedInUser.settings.globalCurrency, isOcrItemizationEnabled)
            deleteUnnecessaryExpenseFormData(updateData)
            if (loggedInUser.activeCompanyProfile.isAdmin && shouldAdjustLimit(expense, new Expense({ ...expense, ...updateData }), card)) {
                expense.shouldUpdateCardLimit = await DialogService.confirmCardLimitUpdate()
            }

            // check for vendor rule to adjust dueOn if needed
            const vendor = VendorService.getVendorById(expense.vendor)
            const paymentTerm = vendor?.rule?.paymentTerm
            const dueDateUpdate =
                !updateData.invoiceDueDate && paymentTerm && updateData.invoiceDate ? { invoiceDueDate: moment(updateData.invoiceDate).add(paymentTerm, "days") } : {}

            await updateExpense(expense.id, new Expense({ ...expense, ...updateData, ...dueDateUpdate, skipEmail: true, isOcrUpdate: true }))(dispatch)
            NotificationService.send(NotificationTypeEnum.INFO, t("notification:ocr.processed.title"), t("notification:ocr.processed_and_updated.message"), 60)
        } catch (err) {
            NotificationService.send(NotificationTypeEnum.WARNING, t("error:ocr.title"), t("error:ocr.message"))
        }
    }

    const doFileUpload = async (file: any, kind: DocumentTypeEnum) => {
        if (!checkFileSize(file)) return

        const isInvoice = kind === DocumentTypeEnum.INVOICE
        const setIsLoading = isInvoice ? setIsInvoiceUploading : setIsDeliveryNoteUploading
        setIsLoading(true)

        try {
            const data = await FileService.upload(file, UploadKindEnum.EXPENSE.toString())
            const newFileData = {
                fileName: file.name,
                url: data.url,
                hash: data.hash,
                uploadDate: new Date(),
            }
            if (await checkForDuplicateInvoiceFile(data.duplicateData)) {
                file["storageName"] = data.url
                expense = await updateDocuments(expense.id, kind, newFileData, OperationTypeEnum.INSERT)(dispatch)
                setExpense(expense)
                NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:file.uploaded.title"), t("notification:file.uploaded.message"))

                if (isOcrEnabled && kind === DocumentTypeEnum.INVOICE) runOcr(data.url)
            }
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:file.upload.title"))
        } finally {
            setIsLoading(false)
        }
    }

    const archiveTags = (
        <>
            {isExpenseArchived(expense, archiveAfterXDays) ? (
                <Tag className="ant-tag-blue mt-4 ml-4 flex flex-row">
                    <ArchiveIcon className="mt-3" size={12} />
                    <span className="mt-2 ml-5"> {t("label:archived")}</span>
                </Tag>
            ) : (
                isExpenseManuallyUnarchived(expense, archiveAfterXDays) && (
                    <Tag className="ant-tag-blue mt-4 ml-4 flex flex-row">
                        <ArchiveIcon className="mt-3 " size={12} />
                        <span className="mt-2 ml-5"> {t("label:manually_unarchived")}</span>
                    </Tag>
                )
            )}
        </>
    )

    const onSummaryDownload = async (menuInfo: any) => {
        try {
            switch (expense.kind) {
                case ExpenseKindEnum.PER_DIEM:
                case ExpenseKindEnum.MILEAGE:
                    setSummaryDownloadLoading(true)
                    await FileService.exportExpenseSummary(expense.id, menuInfo.key)
                    break
                default:
                    break
            }
        } catch (error) {
            console.error(`Error on summary download lang: ${menuInfo.key}, kind: ${expense.kind}, error: ${JSON.stringify(error)}`)
        } finally {
            setSummaryDownloadLoading(false)
        }
    }

    const exportDropDownOverlay = () => (
        <Menu onClick={onSummaryDownload} className="w-150 ml-10">
            <Menu.Item key={UserLanguageEnum.EN} className="flex items-center min-w-150 py-8">
                <div className="flex items-center ml-12">
                    <p className="leading-none">
                        <span className="text-text">{t("label:english")}</span>
                    </p>
                </div>
            </Menu.Item>
            <Menu.Item key={UserLanguageEnum.DE} className="flex items-center min-w-150 py-8">
                <div className="flex items-center ml-12">
                    <p className="leading-none">
                        <span className="text-text">{t("label:german")}</span>
                    </p>
                </div>
            </Menu.Item>
        </Menu>
    )

    const renderDocumentSection = () => {
        if (isMileageExpense(expense)) {
            return (
                <Card className="w-full">
                    <div className="flex justify-between align-center">
                        <p>{t("label:trip_map")}</p>

                        <Dropdown getPopupContainer={getPopupAnchor()} overlay={exportDropDownOverlay} trigger={["click"]} className="flex items-center">
                            <div className={"cursor-pointer"} onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}>
                                <Button
                                    onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
                                    block
                                    loading={isSummaryDownloadLoading}
                                    className={`pointer-events-none btn-default ml-10`}
                                    icon={<DownloadIcon />}
                                    type={"default"}
                                >
                                    <span>
                                        {t("action:mileage_summary")} <i className="el-icon-caret-bottom el-icon--right" />
                                    </span>
                                </Button>
                            </div>
                        </Dropdown>
                    </div>
                    <Divider />
                    <div className="h-600">
                        <MileageRequestMap
                            interactive={false}
                            markers={{
                                stops: expense.stops,
                            }}
                            initialRoute={(expense as any).route}
                        />
                    </div>
                </Card>
            )
        }

        if (isPerDiemExpense(expense)) {
            return (
                <Card className="w-full">
                    <div className="flex justify-between align-center">
                        <p>{t("label:per_diem_calculation")}</p>

                        <Dropdown getPopupContainer={getPopupAnchor()} overlay={exportDropDownOverlay} trigger={["click"]} className="flex items-center justify-center">
                            <div className={"cursor-pointer"} onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}>
                                <Button
                                    onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
                                    block
                                    loading={isSummaryDownloadLoading}
                                    className={`pointer-events-none btn-default ml-10`}
                                    icon={<DownloadIcon />}
                                    type={"default"}
                                >
                                    <span>
                                        {t("action:per_diem_summary")}
                                        <i className="el-icon-caret-bottom el-icon--right" />
                                    </span>
                                </Button>
                            </div>
                        </Dropdown>
                    </div>
                    <Divider />
                    <div className="w-full overflow-x-auto">
                        <PerDiemReimbursementsPerDayTable perDiemExpense={expense} />
                    </div>
                </Card>
            )
        }

        return (
            <ExpenseDocumentSection
                expense={expense}
                doFileUpload={doFileUpload}
                onInboxSelected={onInboxSelected}
                isUploadingInvoice={isInvoiceUploading}
                ocrSettings={{ isOcrEnabled, isOcrItemizationEnabled, setIsOcrEnabled, setIsOcrItemizationEnabled }}
                isOcrUploading={isOcrUploading}
                isReadOnly={isReadOnly}
            />
        )
    }
    const handleRefreshPaymentStatus = async () => {
        try {
            await BankService.refreshPaymentStatus(expense.id)
            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:banking.refresh_payment_status.title"), t("notification:banking.refresh_payment_status.message"))
        } catch {
            NotificationService.send(NotificationTypeEnum.ERROR, t("error:banking.refresh_payment_status.title"), t("error:banking.refresh_payment_status.message"))
        }
    }

    const handleHardResetPaymentStatus = async () => {
        try {
            await BankService.resetPaymentStatus(expense.id)
            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:banking.refresh_payment_status.title"), t("notification:banking.refresh_payment_status.message"))
        } catch {
            NotificationService.send(NotificationTypeEnum.ERROR, t("error:banking.refresh_payment_status.title"), t("error:banking.refresh_payment_status.message"))
        }
    }

    return !expense || !expense.id || expense.id !== id || isLoadingExpense ? (
        <Loading />
    ) : (
        <ReadOnlyContext.Provider value={{ isReadOnly: isReadOnly }}>
            <div>
                <Helmet>
                    <title>{t("label:request") + " #" + expense?.expenseNumber}</title>
                </Helmet>
                {expense.deleted && (
                    <Alert
                        className="mb-20"
                        message={t("notification:request.already_deleted.title")}
                        type="error"
                        description={t("notification:request.already_deleted.message")}
                        closable={false}
                        showIcon={true}
                    />
                )}

                {showPaymentFailedAlert(expense.status, expense.payment) && (
                    <Alert
                        className="mb-20"
                        message={t("error:payment_failed.message")}
                        type="error"
                        description={t("error:payment_failed.description", { message: expense.payment?.bankMessage ?? t("error:payment_failed.none") })}
                        closable={false}
                        showIcon={true}
                    />
                )}
                {expense.isInPaymentProcess && (
                    <Alert
                        className="mb-20"
                        message={
                            <div className="flex items-center justify-between space-x-20">
                                <div>
                                    <p className="font-bold text-base mb-10">{t("label:payment_pending.message")}</p>
                                    <p>{t("label:payment_pending.description")}</p>
                                </div>
                                <div>
                                    <Button type="primary" onClick={handleRefreshPaymentStatus}>
                                        {t("action:banking.refresh_payment_status")}
                                    </Button>
                                    {loggedInUser.email == FINWAY_ADMIN_EMAIL && (
                                        <Button className="mt-10 btn-danger" onClick={handleHardResetPaymentStatus} icon={<AlertTriangle size={14} />}>
                                            {t("action:banking.force_payment_status_reset")}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        }
                        type="info"
                        description={""}
                        closable={false}
                        showIcon={true}
                    />
                )}
                <ExpenseCreateFormModal
                    type={requestType}
                    hideExpenseKindRadioOption={true}
                    isNew={false}
                    isShowing={isEditModalShowing}
                    kind={expense.kind}
                    onCancel={() => setIsEditModalShowing(false)}
                />
                <InboxInvoiceModal
                    isShowing={showInboxModal}
                    onCancel={() => setShowInboxModal(false)}
                    expenses={[expense]}
                    isOcrEnabled={isOcrEnabled}
                    preSelectedExpense={expense}
                    preSelectedInvoice={undefined}
                    shouldResetSelectedInboxOnClose
                />
                <PageCustomHeader
                    title={
                        <div className="flex">
                            <h1>{t("label:request") + " #" + expense?.expenseNumber}</h1>
                            {displayUserAsTagged && <SharedExpenseTag tagging={expense.userTagging?.filter(({ tagged }) => tagged === loggedInUser.id)} />}
                            {loggedInUser.activeCompanyProfile.isAdmin && isMatched(expense) && <Tag className="ant-tag-green mt-4 ml-4">{t("info:matched")}</Tag>}
                            {loggedInUser.activeCompanyProfile.isAdmin && (
                                <ExportTag exportInformation={expense.exportInformation} isInExportProcess={expense.isInExportProcess} className="mt-4 ml-4-10" />
                            )}
                            {archiveTags}
                        </div>
                    }
                    actionButtons={renderActionButtons}
                />

                {(!splitsHasAnyCostCenter || !expense.costCenter) && !expense.vendor && (
                    <Alert
                        className="mb-20"
                        message={<div className="font-bold">{t("info:missing_vendor_or_cc.title")}</div>}
                        type="warning"
                        description={t("info:missing_vendor_or_cc.message")}
                        closable={false}
                        showIcon={true}
                    />
                )}
                {!splitsHasCompleteCostCenter && !!expense.vendor && (
                    <Alert
                        className="mb-20"
                        message={<div className="font-bold">{t("info:missing_cc.title")}</div>}
                        type="warning"
                        description={t("info:missing_cc.message")}
                        closable={false}
                        showIcon={true}
                    />
                )}

                <ExpenseCreditorAlert vendor={vendor} expense={expense} />

                {expense.id && (
                    <>
                        <Row>
                            <Col span={24}>
                                <MainDetailsTabs expense={expense} setIsExpenseUpdating={setIsExpenseUpdating} setIsEditModalShowing={setIsEditModalShowing} />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <div className="grid md:grid-cols-5 lg:grid-cols-9 gap-24">
                                    <div className="md:col-span-2 lg:col-span-3">
                                        <div className="card-list">
                                            <ExpenseRightSection
                                                expense={expense}
                                                loggedInUser={loggedInUser}
                                                vendor={vendor!}
                                                approver={approver}
                                                requestedBy={requestedBy}
                                                requestType={requestType}
                                                costCenter={costCenter}
                                                costCenter2={costCenter2}
                                                isInvoiceUploading={isInvoiceUploading}
                                                isDeliveryNoteUploading={isDeliveryNoteUploading}
                                                isLoading={isLoading}
                                                nextExpense={nextExpense}
                                                cancelRequest={onCancelRequest}
                                                sendEmailReminder={onSendEmailReminder}
                                                markRequestAsApproved={markRequestAsApproved}
                                                markRequestAsReviewed={markRequestAsReviewed}
                                                markRequestAsPaid={markRequestAsPaid}
                                                doFileUpload={doFileUpload}
                                                payExpense={payExpense}
                                                onInboxSelected={onInboxSelected}
                                                isOcrUploading={isOcrUploading}
                                                isExpenseUpdating={isExpenseUpdating}
                                                setIsExpenseUpdating={setIsExpenseUpdating}
                                            />
                                        </div>
                                    </div>
                                    <div className="md:col-span-3 lg:col-span-6 md:row-span-2 md:order-first">{renderDocumentSection()}</div>
                                    <div className="md:col-span-2 lg:col-span-3">
                                        <Suspense fallback={<></>}>
                                            <CommentContainer type={CollectionNameEnum.EXPENSE} comments={expense.comments} loggedInUser={loggedInUser} object={expense} />
                                        </Suspense>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </>
                )}
                {isShowingPaymentConfirmationModal && (
                    <PaymentConfirmationModal
                        isScheduledPayment={isScheduledPayment}
                        isVisible={isShowingPaymentConfirmationModal}
                        expenses={[expense]}
                        onCancel={() => setIsShowingPaymentConfirmationModal(false)}
                    />
                )}
            </div>
        </ReadOnlyContext.Provider>
    )
}

export default ExpenseDetails
